import { useTranslations } from "@zapier/i18n";
import { useEffect, useState } from "react";
import {
  Badge,
  BetaDataTableCell as Cell,
  VisuallyHidden,
} from "@zapier/design-system";
import {
  FolderContext,
  FolderTreeItem,
  useCurrentUser,
  useFolderActionPermissions,
} from "@zapier/client";
import { OwnerCell, Row, TitleCell, FolderModal } from "@zapier/shared";
import { AssetTypes, DEFAULT_FOLDER_ID, getFolderUrl } from "@zapier/utils";

import { useFolderActionItems } from "./useFolderActionItems";
import styles from "./FolderRow.module.css";

interface Props {
  folder: FolderTreeItem;
  folderLevel: number;
  folderZapTableRow?: boolean;
  isFolderExpanded?: boolean;
  isFoldersExpandedByDefault?: boolean;
  onExpand?: (isFolderExpanded: boolean) => void;
  searchQuery?: string | null;
  includeSelectionColumn?: boolean;
  setCurrentModal: (modal: FolderModal | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
  isChildRow?: boolean;
}

const FolderRowBranch = ({
  folder,
  folderLevel,
  folderZapTableRow,
  isFolderExpanded,
  onExpand,
  searchQuery,
  includeSelectionColumn,
  setCurrentModal,
  setCurrentFolder,
  isChildRow,
}: Props) => {
  const t = useTranslations("folder-row");
  const { user } = useCurrentUser();

  // Drive the menu's loading affordance from THIS permissions instance — the one whose
  // `enablePermissionsCheck` is wired to `onOpenMenu` below. Opening the menu enables it,
  // so its `isLoading` reliably reflects the in-flight permissions request. (The instance
  // inside `useFolderActionItems` is never enabled, so its loading state can't be relied on
  // to drive the affordance.) Kept undestructured so `.isLoading` reads next to its owning
  // hook rather than as a renamed standalone flag.
  const folderActionPermissions = useFolderActionPermissions({
    folderId: folder.id,
    folderDepth: folderLevel,
  });

  const folderActionItems = useFolderActionItems({
    folder,
    folderLevel,
    setCurrentFolder,
    setCurrentModal,
  });

  return (
    <Row
      allowsSelection={false}
      hiddenSelection={includeSelectionColumn}
      id={String(folder.id)}
      assetName={folder.name}
      kebabMenuLabel={t("labels.folder-options")}
      kebabMenuAriaLabel={t("labels.folder-options-for", { name: folder.name })}
      menuItems={folderActionItems}
      isMenuLoading={folderActionPermissions.isLoading}
      onOpenMenu={folderActionPermissions.enablePermissionsCheck}
      assetType={AssetTypes.Folder}
      className={`${isChildRow ? styles.childRow : ""} ${folderZapTableRow ? styles.folderZapTableRow : ""}`}
    >
      <TitleCell
        color="GrayWarm6"
        icon={folder.isShared ? "team" : "navFolder"}
        title={folder.name}
        searchQuery={searchQuery}
        url={getFolderUrl(
          folder.id,
          folder.id === DEFAULT_FOLDER_ID && folder.ownerId !== user?.id
            ? folder.ownerId
            : undefined,
        )}
        id={folder.id}
        accordionLevel={folderLevel}
        hasAccordion={!!folder.children?.length}
        allowAccordion={true}
        isAccordionExpanded={isFolderExpanded}
        onExpandAccordion={onExpand}
        hiddenSelection={includeSelectionColumn}
        assetType={AssetTypes.Folder}
      />
      {!folderZapTableRow && (
        <Cell className={styles.assetCountCell}>
          <Badge variant="neutral">
            {(folder.assetCount ?? 0).toLocaleString()}
          </Badge>
        </Cell>
      )}
      {/* Three empty cells for Apps, Last modified, and Status when FolderRow is rendered in FolderZapTable */}
      {folderZapTableRow && (
        <>
          <Cell>
            <VisuallyHidden>{t("columns.apps")}</VisuallyHidden>
          </Cell>
          <Cell>
            <VisuallyHidden>{t("columns.last-modified")}</VisuallyHidden>
          </Cell>
          <Cell>
            <VisuallyHidden>{t("columns.status")}</VisuallyHidden>
          </Cell>
        </>
      )}
      <OwnerCell
        name={folder.owner?.fullName}
        photoUrl={folder.owner?.photoUrl}
      />
    </Row>
  );
};

const FolderRow = ({
  folder,
  folderLevel = 0,
  folderZapTableRow,
  searchQuery,
  isFoldersExpandedByDefault,
  includeSelectionColumn,
  setCurrentFolder,
  setCurrentModal,
  isChildRow,
}: Props) => {
  const [isFolderExpanded, setIsFolderExpanded] = useState(false);

  useEffect(() => {
    // Expand folders by default if search query is present
    if (isFoldersExpandedByDefault) {
      setIsFolderExpanded(true);
    } else {
      // Close folders if search query is removed
      setIsFolderExpanded(false);
    }
  }, [folder, isFoldersExpandedByDefault]);

  return (
    <FolderContext.Provider value={folder}>
      <FolderRowBranch
        folder={folder}
        folderLevel={folderLevel}
        folderZapTableRow={folderZapTableRow}
        isFolderExpanded={isFolderExpanded}
        onExpand={setIsFolderExpanded}
        searchQuery={searchQuery}
        includeSelectionColumn={includeSelectionColumn}
        setCurrentFolder={setCurrentFolder}
        setCurrentModal={setCurrentModal}
        isChildRow={isChildRow}
      />
      {/* Recursively render children in the same row */}
      {isFolderExpanded &&
        folder?.children?.map((child) => (
          <FolderRow
            folder={child as FolderTreeItem}
            folderLevel={folderLevel + 1}
            folderZapTableRow={folderZapTableRow}
            isFoldersExpandedByDefault={isFoldersExpandedByDefault}
            key={child.id}
            searchQuery={searchQuery}
            includeSelectionColumn={includeSelectionColumn}
            setCurrentFolder={setCurrentFolder}
            setCurrentModal={setCurrentModal}
            isChildRow={true}
          />
        ))}
    </FolderContext.Provider>
  );
};

export default FolderRow;
